import React, { useEffect, useState } from 'react';

import { Select } from '@aline/common';
import { Typography } from '@mui/material';
import { AlineButton, AnimatedAlineDialog } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { removeWorkflowActionsFromCache } from 'src/gql/workflow-action-cache';
import { useGetUnusedWorkflowActions } from 'src/hooks';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { useSanityModalStyles } from './helpers';
import { createUnusedActionsCopy } from './unused-actions-copy';

interface UnusedActionsDialogProps {
  onClose: () => void;
  open: boolean;
  workflowId: UUID;
}

export function UnusedActionsDialog({ onClose, open, workflowId }: UnusedActionsDialogProps) {
  const { classes } = useSanityModalStyles();

  return (
    <AnimatedAlineDialog
      classes={{ paper: classes.dialog }}
      data-cy={Select.document.s4s.bottomBar.sanityCheck.unusedActions.container}
      open={open}
      title="Remove unused signers?"
      onClose={onClose}
    >
      <UnusedActionsBody workflowId={workflowId} onClose={onClose} />
    </AnimatedAlineDialog>
  );
}

const useUnusedActionsStyles = makeStyles()(theme => ({
  listItem: {
    ...getTypographyForSx(theme.typography.body1),
  },
}));

interface UnusedActionsBodyProps {
  onClose: () => void;
  workflowId: UUID;
}

function UnusedActionsBody({ onClose, workflowId }: UnusedActionsBodyProps): JSX.Element {
  const { classes } = useUnusedActionsStyles();
  const { classes: sanityClasses } = useSanityModalStyles();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { unusedActions } = useGetUnusedWorkflowActions(workflowId);
  const [unusedActionMsgList, setUnusedActionsMsgList] = useState<string[]>(
    createUnusedActionsCopy(unusedActions)
  );
  const [clearUnusedActions] = Apollo.useClearUnusedWorkflowActionsMutation();

  useEffect(() => {
    // Don't update msg while submitting.  Avoids weird flicker where Workflow.unusedActions is cleared while submitting.
    if (isSubmitting) return;
    setUnusedActionsMsgList(createUnusedActionsCopy(unusedActions));
  }, [isSubmitting, unusedActions]);

  return (
    <>
      <Typography>
        The signers below have no fields in any document. Click to remove them from the workflow and
        continue.
      </Typography>
      <ol>
        {unusedActionMsgList.map((msg, index) => (
          <li key={index} className={classes.listItem}>
            {msg}
          </li>
        ))}
      </ol>
      <div className={sanityClasses.buttonRow}>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unusedActions.cancelButton}
          disabled={isSubmitting}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          data-cy={Select.document.s4s.bottomBar.sanityCheck.unusedActions.nextButton}
          loading={isSubmitting}
          variant="contained"
          onClick={async () => {
            setIsSubmitting(true);
            const clearedActions = unusedActions.map(({ id }) => ({
              __typename: 'WorkflowRecipient' as const,
              id,
            }));
            await clearUnusedActions({
              update: cache => removeWorkflowActionsFromCache(cache, clearedActions),
              variables: { workflowId },
            });
            onClose();
            setIsSubmitting(false);
          }}
        >
          Remove Signers
        </AlineButton>
      </div>
    </>
  );
}
